import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { fromJson } from "../models/modelHelpers";
import { httpClient, Service } from "./client";
import { CMSAsset } from "../models/CMS/cmsAsset";
import { CMSTag } from "../models/CMS/cmsTag";
import { CMSMedia } from "../models/CMS/cmsMedia";
import { CMSEPGEvent } from "../models/CMS/cmsEPGEvent";
import { CMSGenre } from "../models/CMS/cmsGenre";
import { CMSCollection } from "../models/CMS/cmsCollection";
import { CMSPagination } from "@nendaTypes/cms/CmsPaginationType";
import { Provider } from "@client/components/CustomerPortal/store/reducers/cmsReducer";

export interface DashMpdMetadata {
  mediaPresentationDurationMs?: number;
  minBufferTimeMs?: number;
  type?: string;
  profiles?: string;
  baseUrl?: string;
  periods: Array<{
    id?: string;
    start?: string;
    duration?: string;
    durationMs?: number;
    adaptationSets: Array<{
      contentType?: string;
      mimeType?: string;
      codecs?: string;
      width?: number;
      height?: number;
      frameRate?: string;
      par?: string;
      audioSamplingRate?: string;
      representations: Array<{
        id?: string;
        bandwidth?: number;
        width?: number;
        height?: number;
        frameRate?: string;
        codecs?: string;
        mimeType?: string;
        audioSamplingRate?: string;
      }>;
      contentProtection?: Array<{ schemeIdUri?: string; value?: string }>;
    }>;
  }>;
  suggested?: {
    durationMillis?: number;
    videoWidth?: number;
    videoHeight?: number;
    videoFrameRate?: string;
    audioLanguages?: string[];
    subtitleLanguages?: string[];
  };
}

const AZURE_BLOCK_SIZE_BYTES = 8 * 1024 * 1024;

class CmsService extends Service {
  //assets
  queryAssets(
    queryData: any
  ): Observable<{ pagination: CMSPagination; assets: Array<CMSAsset> }> {
    return httpClient.post(`/cms/assets/query`, queryData, this.authHeader());
  }

  getAssets(
    query: string
  ): Observable<{ pagination: CMSPagination; assets: Array<CMSAsset> }> {
    return httpClient.get(`/cms/assets?${query}`, this.authHeader());
  }

  getAsset(id: string): Observable<CMSAsset> {
    return httpClient.get(`/cms/assets/${id}`, this.authHeader());
  }

  getDashMetadata(mpdUrl: string): Observable<DashMpdMetadata> {
    const encoded = encodeURIComponent(mpdUrl);
    return httpClient.get(
      `/cms/assets/dash-metadata?url=${encoded}`,
      this.authHeader()
    );
  }

  createAsset(assetData: { name: string; type: string }): Observable<CMSAsset> {
    return httpClient
      .post(
        `/cms/assets`,
        {
          originalTitle: assetData.name,
          type: assetData.type,
          externalId: name,
          sourceType: "vod",
          countryOfOrigin: "en",
          originalLanguage: "en",
          copyrightInfo: "",
          parentalRatings: [],
          localizedInfo: [],
          medias: [],
          images: [],
          contentRights: [],
          tags: [],
        },
        this.authHeader()
      )
      .pipe(
        map((asset) => {
          return fromJson(CMSAsset, asset) as CMSAsset;
        })
      );
  }

  updateAsset(asset: CMSAsset): Observable<CMSAsset> {
    return httpClient.patch(
      `/cms/assets/${asset.assetId}`,
      asset,
      this.authHeader()
    );
  }

  deleteAsset(id: string): Observable<any> {
    return httpClient.delete(`/cms/assets/${id}`, this.authHeader());
  }
  //providers
  getProviders(): Observable<Provider[]> {
    return httpClient.get(`/cms/providers`, this.authHeader());
  }
  //medias
  createMedia(assetId: string, media: CMSMedia): Observable<any> {
    return httpClient.post(
      `/cms/assets/${assetId}/medias`,
      media,
      this.authHeader()
    );
  }

  updateMedia(assetId: string, media: CMSMedia): Observable<CMSMedia> {
    return httpClient.put(
      `/cms/assets/${assetId}/medias/${media.mediaId}`,
      media,
      this.authHeader()
    );
  }

  deleteMedia(assetId: string, mediaId: string): Observable<any> {
    return httpClient.delete(
      `/cms/assets/${assetId}/medias/${mediaId}`,
      this.authHeader()
    );
  }

  //channels
  getChannels(query: string): Observable<{
    assets: Array<CMSAsset>;
    pagination: CMSPagination;
  }> {
    return httpClient.get(`/cms/channels?${query}`, this.authHeader());
  }

  getChannel(id: string): Observable<CMSAsset> {
    return httpClient.get(`/cms/channels/${id}`, this.authHeader());
  }

  createChannel(name: string): Observable<CMSAsset> {
    return httpClient.post(
      `/cms/channels`,
      {
        originalTitle: name,
        externalId: name,
        type: "channel",
        sourceType: "live",
        countryOfOrigin: "en",
        originalLanguage: "en",
        copyrightInfo: "",
        parentalRatings: [],
        localizedInfo: [],
        medias: [],
        images: [],
        contentRights: [],
        tags: [],
        provider: {
          providerId: "Nenda",
        },
      },
      this.authHeader()
    );
  }

  updateChannel(channel: CMSAsset): Observable<CMSAsset> {
    return httpClient.patch(
      `/cms/channels/${channel.assetId}`,
      channel,
      this.authHeader()
    );
  }

  deleteChannel(id: string): Observable<any> {
    return httpClient.delete(`/cms/channels/${id}`, this.authHeader());
  }

  //EPg
  getEPG(id: string): Observable<CMSEPGEvent[]> {
    return httpClient.get(`/cms/channels/${id}/epgevents`, this.authHeader());
  }

  //tags
  getTags(query: string): Observable<{
    tags: Array<CMSTag>;
    pagination: CMSPagination;
  }> {
    return httpClient.get(
      `/cms/tags${query ? "?" + query : ""}`,
      this.authHeader()
    );
  }

  getTag(id: string): Observable<CMSTag> {
    return httpClient.get(`/cms/tags/${id}`, this.authHeader());
  }

  createTag(tagData: { name: string; type: string }): Observable<CMSTag> {
    return httpClient.post(
      `/cms/tags`,
      {
        name: tagData.name,
        type: tagData.type,
      },
      this.authHeader()
    );
  }

  updateTag(tag: CMSTag): Observable<CMSTag> {
    return httpClient.put(`/cms/tags/${tag.tagId}`, tag, this.authHeader());
  }

  deleteTag(id: string): Observable<void> {
    return httpClient.delete(`/cms/tags/${id}`, this.authHeader());
  }

  getGenres(): Observable<CMSGenre[]> {
    return httpClient.get("/cms/genres", this.authHeader());
  }

  //collection
  getCollections(searchQuery: string): Observable<CMSCollection[]> {
    return httpClient.get(
      `/cms/collections${searchQuery ? "?searchQuery=" + searchQuery : ""}`,
      this.authHeader()
    );
  }

  getCollection(id: string): Observable<CMSCollection> {
    return httpClient.get(`/cms/collections/${id}`, this.authHeader());
  }

  createCollection(name: string): Observable<CMSCollection> {
    return httpClient.post(
      `/cms/collections`,
      {
        externalId: name,
        name: name,
      },
      this.authHeader()
    );
  }

  updateCollection(collection: CMSCollection): Observable<CMSCollection> {
    return httpClient.put(
      `/cms/collections/${collection._id}`,
      collection,
      this.authHeader()
    );
  }

  deleteCollection(id: string): Observable<void> {
    return httpClient.delete(`/cms/collections/${id}`, this.authHeader());
  }

  getAssetCollections(assetId: string): Observable<CMSCollection[]> {
    return httpClient.get(`/cms/vod/${assetId}/collections`, this.authHeader());
  }

  updateAssetCollections(
    assetId: string,
    collectionIds: string[]
  ): Observable<CMSCollection[]> {
    return httpClient.put(
      `/cms/vod/${assetId}/collections`,
      { collectionIds },
      this.authHeader()
    );
  }

  uploadFile(id: string, file: File, path: string) {
    const formData = new FormData();
    formData.append("path", path);
    formData.append("file", file);

    return httpClient.put(
      `/cms/assets/${id}/file`,
      formData,
      {
        ...this.authHeader(),
      },
      false
    );
  }

  requestStorageUploadUrl(params: {
    fileName: string;
    contentType?: string;
    objectPrefix?: string;
    expiresInSeconds?: number;
  }) {
    return httpClient.post(`/cms/vod/upload-url`, params, {
      ...this.authHeader(),
    });
  }

  triggerVodIngest(params: {
    sourceUrl: string;
    assetId: string;
    pipelineName?: string;
    vodOptions?: Record<string, unknown>;
    entitlements?: string[];
  }) {
    return httpClient.post(`/cms/vod/trigger`, params, {
      ...this.authHeader(),
    });
  }

  getVodStatus(jobId: string) {
    const query = new URLSearchParams({ jobId });
    return httpClient.get(
      `/cms/vod/status?${query.toString()}`,
      this.authHeader()
    );
  }

  getActiveIngestJob(assetId: string) {
    const query = new URLSearchParams({ assetId });
    return httpClient.get(
      `/cms/vod/jobs/active?${query.toString()}`,
      this.authHeader()
    );
  }

  retryIngestJob(jobId: string) {
    return httpClient.post(
      `/cms/vod/jobs/${jobId}/retry`,
      {},
      this.authHeader()
    );
  }

  uploadSubtitles(assetId: string, file: File, language?: string) {
    const formData = new FormData();
    formData.append("file", file);
    const url =
      language && language.trim()
        ? `/cms/subtitles/upload/${assetId}?language=${encodeURIComponent(language.trim())}`
        : `/cms/subtitles/upload/${assetId}`;
    return httpClient.post(url, formData, this.authHeader(), false);
  }

  async uploadFileToPresignedUrl(params: {
    uploadUrl: string;
    file: File;
    onProgress?: (percent: number) => void;
  }): Promise<void> {
    const totalBlocks = Math.ceil(params.file.size / AZURE_BLOCK_SIZE_BYTES);
    const blockIds: string[] = [];

    for (let blockIndex = 0; blockIndex < totalBlocks; blockIndex += 1) {
      const start = blockIndex * AZURE_BLOCK_SIZE_BYTES;
      const end = Math.min(start + AZURE_BLOCK_SIZE_BYTES, params.file.size);
      const chunk = params.file.slice(start, end);

      const blockId = btoa(`block-${String(blockIndex).padStart(8, "0")}`);
      blockIds.push(blockId);

      const blockUrl = new URL(params.uploadUrl);
      blockUrl.searchParams.set("comp", "block");
      blockUrl.searchParams.set("blockid", blockId);

      const blockResponse = await fetch(blockUrl.toString(), {
        method: "PUT",
        body: chunk,
        headers: {
          "Content-Type": "application/octet-stream",
        },
      });

      if (!blockResponse.ok) {
        throw new Error(
          `Upload block ${blockIndex + 1}/${totalBlocks} failed with status ${
            blockResponse.status
          }`
        );
      }

      params.onProgress?.(Math.round(((blockIndex + 1) / totalBlocks) * 100));
    }

    const blockListXml =
      '<?xml version="1.0" encoding="utf-8"?><BlockList>' +
      blockIds.map((blockId) => `<Latest>${blockId}</Latest>`).join("") +
      "</BlockList>";

    const commitUrl = new URL(params.uploadUrl);
    commitUrl.searchParams.set("comp", "blocklist");

    const commitResponse = await fetch(commitUrl.toString(), {
      method: "PUT",
      body: blockListXml,
      headers: {
        "Content-Type": "application/xml",
        "x-ms-blob-content-type":
          params.file.type || "application/octet-stream",
      },
    });

    if (!commitResponse.ok) {
      throw new Error(
        `Finalize upload failed with status ${commitResponse.status}`
      );
    }
  }
}

export const cmsService = new CmsService();
